import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const SIGNALS = [
  { name: "Direction", anchorY: 18, scatterY: 72, stabiliseY: 36, shape: "circle", colour: "#C8553D" },
  { name: "Standards", anchorY: 52, scatterY: 18, stabiliseY: 40, shape: "square", colour: "#A95A47" },
  { name: "Ownership", anchorY: 86, scatterY: 136, stabiliseY: 103, shape: "diamond", colour: "#45484A" },
  { name: "Authority", anchorY: 120, scatterY: 54, stabiliseY: 98, shape: "triangle", colour: "#92745F" },
  { name: "Behaviour", anchorY: 154, scatterY: 146, stabiliseY: 151, shape: "hexagon", colour: "#746D66" },
];

const Shape = ({ shape, colour }) => {
  if (shape === "triangle") {
    return <span className="block h-0 w-0 border-l-[6px] border-r-[6px] border-b-[11px] border-l-transparent border-r-transparent" style={{ borderBottomColor: colour }} />;
  }
  const shapes = {
    circle: "rounded-full",
    square: "",
    diamond: "rotate-45",
    hexagon: "[clip-path:polygon(25%_0,75%_0,100%_50%,75%_100%,25%_100%,0_50%)]",
  };
  return <span className={`block h-3 w-3 shrink-0 ${shapes[shape]}`} style={{ backgroundColor: colour }} />;
};

export const FrictionClarityDiagram = () => {
  const diagramRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const isInView = useInView(diagramRef, { amount: 0.2 });
  const animateCycle = !reduceMotion && isInView;

  return (
    <div
      data-testid="friction-clarity-diagram"
      ref={diagramRef}
      role="img"
      aria-label="Direction, Standards, Ownership, Authority and Behaviour begin aligned, scatter as friction builds, stabilise through BOS and return to aligned stable execution."
      className="border-y border-rule py-8 md:py-12 overflow-hidden"
    >
      <div className="grid grid-cols-4 gap-2 text-[8px] sm:text-[10px] md:text-[12px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.14em]">
        <span className="text-mute">Work begins</span>
        <span className="text-center text-terra">Friction builds</span>
        <span className="text-center text-terra">BOS stabilises</span>
        <span className="text-right text-ink">Stable execution</span>
      </div>
      <div className="relative mt-8 h-48 md:h-56" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "linear-gradient(rgba(15,17,21,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(15,17,21,0.045) 1px, transparent 1px)",
            backgroundSize: "64px 34px",
          }}
        />
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1000 200" preserveAspectRatio="none">
          {SIGNALS.map((signal) => (
            <line
              key={`${signal.name}-reference`}
              x1="0"
              x2="1000"
              y1={signal.anchorY + 7}
              y2={signal.anchorY + 7}
              stroke={signal.colour}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              opacity="0.18"
            />
          ))}
        </svg>
        <div className="absolute inset-y-2 left-1/4 w-px bg-ink/15" />
        <div className="absolute inset-y-2 left-1/2 w-px bg-terra/30" />
        <div className="absolute inset-y-2 left-3/4 w-px bg-ink/15" />
        {SIGNALS.map((signal) => (
          <div key={`${signal.name}-phase-markers`}>
            <span className="absolute left-0 bg-paper/90 pr-2 text-[9px] font-medium text-mute sm:text-[11px]" style={{ top: signal.anchorY }}>{signal.name}</span>
            <span className="absolute left-[12%] opacity-20" style={{ top: signal.anchorY }}><Shape shape={signal.shape} colour={signal.colour} /></span>
            <span className="absolute left-[37%] opacity-[0.18]" style={{ top: signal.scatterY }}><Shape shape={signal.shape} colour={signal.colour} /></span>
            <span className="absolute left-[62%] opacity-25" style={{ top: signal.stabiliseY }}><Shape shape={signal.shape} colour={signal.colour} /></span>
            <span className="absolute right-0 bg-paper/90 pl-2 text-right text-[9px] font-medium text-ink sm:text-[11px]" style={{ top: signal.anchorY }}>{signal.name}</span>
          </div>
        ))}
        {SIGNALS.map((signal, index) => (
          <motion.div
            key={signal.name}
            data-dimension={signal.name.toLowerCase()}
            className="absolute left-0"
            initial={{ left: reduceMotion ? "86%" : "12%", y: signal.anchorY, opacity: reduceMotion ? 1 : 0.45 }}
            animate={reduceMotion
              ? { left: "86%", y: signal.anchorY, opacity: 1 }
              : animateCycle
                ? {
                    left: ["12%", "37%", "62%", "86%", "86%", "86%", "12%", "12%"],
                    y: [signal.anchorY, signal.scatterY, signal.stabiliseY, signal.anchorY, signal.anchorY, signal.anchorY, signal.anchorY, signal.anchorY],
                    opacity: [0.45, 0.8, 0.9, 1, 1, 0, 0, 0.45],
                  }
                : { left: "12%", y: signal.anchorY, opacity: 0.45 }}
            transition={reduceMotion
              ? { duration: 0 }
              : animateCycle
                ? {
                    duration: 16,
                    delay: 1.5 + index * 0.08,
                    times: [0, 0.28, 0.5, 0.68, 0.84, 0.9, 0.94, 1],
                    ease: [0.45, 0, 0.2, 1],
                    repeat: Infinity,
                    repeatType: "loop",
                  }
                : { duration: 0.35 }}
          >
            <Shape shape={signal.shape} colour={signal.colour} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};
